import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { alpha, Box, Button, ButtonProps, Skeleton, useTheme } from "@mui/material";
import { type BoxRegion } from "components/Document/ReviewInteraction";
import { memo, type RefObject, useCallback, useEffect, useRef, useState } from "react";
import { Document } from "react-pdf";

import type { PreviewFitMode } from "./previewFitMode";

import { useKeepPlaceOnZoom } from "./keepPlaceOnZoom";
import { usePageClip } from "./pageClip";
import { PDF_DOCUMENT_OPTIONS } from "./pdfjs";
import { pageInView, revealMatch, scrollToPage } from "./pdfNavigation";
import { PdfPage } from "./PdfPage";
import { highlightText } from "./pdfSearch";
import { PreviewError } from "./PreviewError";
import { PreviewZoomBar } from "./PreviewZoomBar";
import { useFramedRegion } from "./useFramedRegion";
import { type PdfFailure, usePdfDocument } from "./usePdfDocument";
import { type SearchablePdf, usePdfSearch } from "./usePdfSearch";
import { clampZoom } from "./zoom";

/**
 * A region of one page to point the reader at.
 *
 * A hint, never a crop. The region comes from the fixed layout of a printed form
 * rather than from anything measured on this document, so a rotated, cropped or
 * photographed scan lands it imprecisely — and those are the documents a reader
 * most needs pointing at. It is drawn as a soft outline over a page that still
 * pans and zooms normally.
 */
export interface DocumentPreviewHighlight {
  /** 1-based page the region is on. */
  page: number;
  region: BoxRegion;
  /** What the outline is pointing at, e.g. "Box 11 · Total quantity". */
  label?: string;
}

/** Shared so a caller with nothing to outline does not hand this a new array each render. */
const EMPTY_HIGHLIGHTS: readonly DocumentPreviewHighlight[] = [];

export interface DocumentPreviewProps {
  fileUrl: string;
  bottomLeftButtonProps?: ButtonProps & {
    text: string;
    toolTipText?: string;
  };
  /**
   * Initial zoom applied once the PDF loads: a scale, or `"page-fit"` /
   * `"page-width"` to fit the first page to the pane. Defaults to actual size,
   * shrunk to fit the pane's width when the page is wider than it.
   */
  defaultScale?: number | PreviewFitMode;
  /**
   * Regions to outline. Usually one — the field the reviewer is in — but the
   * region-verification sheet draws the whole map at once.
   */
  highlights?: readonly DocumentPreviewHighlight[];
  /**
   * The region to bring into view: its page, then a zoom that frames it.
   *
   * Separate from `highlights` because drawing and framing are different jobs.
   * Changing it moves the viewer; clearing it leaves the reader wherever they
   * are rather than scrolling them back, since losing your place is worse than a
   * stale outline.
   */
  frame?: DocumentPreviewHighlight | null;
  /**
   * How many pages the file turned out to have, once it has loaded.
   *
   * Only the viewer knows: nothing on the server records a page count for these
   * documents, and a caller deciding where on one something sits has no other way
   * to learn that there is only one page it could be on.
   */
  onPageCount?: (pageCount: number) => void;
}

/** How tall the loading placeholder is when the pane has no fixed height to fill. */
const LOADING_MIN_HEIGHT = 240;

const FAILURE_MESSAGES: Record<PdfFailure, string | undefined> = {
  unreadable: undefined,
  password: "This PDF is password-protected and can't be previewed.",
};

/**
 * Once per document: `onArrive` and `onPageCount` as soon as it is laid out, and
 * `onSized` once every page has its own size — framing waits for that, so it
 * measures the page it frames.
 */
function useDocumentArrival({
  pdf,
  sized,
  onArrive,
  onPageCount,
  onSized,
}: {
  pdf: { numPages: number } | null;
  sized: boolean;
  onArrive: () => void;
  onPageCount: ((pageCount: number) => void) | undefined;
  onSized: () => void;
}) {
  const arrivedRef = useRef<object | null>(null);
  useEffect(() => {
    if (pdf == null || arrivedRef.current === pdf) return;
    arrivedRef.current = pdf;
    onArrive();
    onPageCount?.(pdf.numPages);
  }, [pdf, onArrive, onPageCount]);

  const sizedRef = useRef<object | null>(null);
  useEffect(() => {
    if (pdf == null || !sized || sizedRef.current === pdf) return;
    sizedRef.current = pdf;
    onSized();
  }, [pdf, sized, onSized]);
}

/** Search over the document: matches marked in each page's text, and the active one revealed. */
function usePaneSearch(pdf: SearchablePdf | null, viewportRef: RefObject<HTMLElement | null>) {
  const { controls, activeMatch, matchesByItem } = usePdfSearch(pdf);
  const renderHighlights = useCallback(
    ({ str, itemIndex, pageIndex }: { str: string; itemIndex: number; pageIndex: number }) =>
      highlightText(str, matchesByItem(pageIndex, itemIndex), activeMatch),
    [matchesByItem, activeMatch]
  );
  // With nothing to mark, let react-pdf render text itself rather than parse every item as HTML.
  const customTextRenderer = controls.matchCount > 0 ? renderHighlights : undefined;

  useEffect(() => {
    const viewport = viewportRef.current;
    if (activeMatch == null || viewport == null) return;
    return revealMatch(viewport, activeMatch);
  }, [activeMatch, viewportRef]);

  return { controls, customTextRenderer };
}

/**
 * Memoized because rendering this is expensive and its surroundings re-render
 * constantly.
 *
 * A review screen re-renders its dialog for reasons that have nothing to do
 * with the document — a validation pass finishing, focus moving to another
 * field — and re-rendering this re-lays out every page, which is slow enough to
 * feel as a delay before a clicked field takes the caret.
 *
 * The comparison is the default shallow one, so a caller must hand this stable
 * props — `highlights` in particular, which is easy to rebuild by accident.
 */
export const DocumentPreview = memo(function DocumentPreview({
  fileUrl,
  bottomLeftButtonProps,
  defaultScale,
  highlights = EMPTY_HIGHLIGHTS,
  frame,
  onPageCount,
}: DocumentPreviewProps) {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  /** The same element as state, so its clip can be measured once it exists. */
  const [viewportElement, setViewportElement] = useState<HTMLDivElement | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const {
    pdf,
    pageSizes,
    sized,
    zoom,
    setZoom,
    fit,
    failure,
    onLoadSuccess,
    onLoadError,
    onPassword,
  } = usePdfDocument(fileUrl, defaultScale, scrollRef);
  const pageClip = usePageClip(viewportElement, pageSizes, zoom);
  const { holdPlace, releasePlace } = useKeepPlaceOnZoom(scrollRef, zoom);

  const {
    viewportRef,
    attachFramedOutline,
    frameKey,
    onDocumentLoad,
    notePageSize,
    noteScale,
    noteReviewerZoom,
  } = useFramedRegion({
    fileUrl,
    frame,
    zoomTo: useCallback(
      (scale: number) => {
        // Framing centres its region after zooming, so the reader's place is not kept.
        releasePlace();
        setZoom(clampZoom(scale));
      },
      [releasePlace, setZoom]
    ),
    jumpToPage: useCallback((pageIndex: number) => scrollToPage(scrollRef.current, pageIndex), []),
  });

  const attachViewport = useCallback(
    (element: HTMLDivElement | null) => {
      scrollRef.current = element;
      viewportRef.current = element;
      setViewportElement(element);
    },
    [viewportRef]
  );

  // Noted while rendering so the framing effect, which runs before this
  // component's own, already sees the size of the page it is about to frame.
  notePageSize(pageSizes?.[(frame?.page ?? 1) - 1] ?? null);

  useEffect(() => {
    noteScale(zoom);
  }, [zoom, noteScale]);

  useDocumentArrival({
    pdf,
    sized,
    onArrive: useCallback(() => {
      setCurrentPage(0);
      const viewport = scrollRef.current;
      if (viewport == null) return;
      viewport.scrollTop = 0;
      viewport.scrollLeft = 0;
    }, []),
    onPageCount,
    onSized: onDocumentLoad,
  });

  const { controls, customTextRenderer } = usePaneSearch(pdf, scrollRef);

  const onScroll = useCallback(() => {
    if (scrollRef.current != null) setCurrentPage(pageInView(scrollRef.current));
  }, []);

  const goToPage = useCallback((pageIndex: number) => {
    setCurrentPage(pageIndex);
    scrollToPage(scrollRef.current, pageIndex);
  }, []);

  /** A zoom the reviewer asked for: framing leaves it to them, and their place is kept. */
  const reviewerZoom = (apply: () => void) => {
    noteReviewerZoom();
    holdPlace();
    apply();
  };

  return (
    <Box
      data-testid="pdf-preview"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      {pageSizes != null && (
        <PreviewZoomBar
          zoom={zoom}
          onZoomChange={(scale) => reviewerZoom(() => setZoom(scale))}
          onFitWidth={() => reviewerZoom(() => fit("page-width", currentPage))}
          onFitHeight={() => reviewerZoom(() => fit("page-fit", currentPage))}
          currentPage={currentPage}
          totalPages={pageSizes.length}
          onPageChange={goToPage}
          allowPageInput
          search={controls}
        />
      )}
      {/*
        In flow rather than pinned, so it fills a parent with a fixed height and
        grows to its pages in one without. The button sits outside the scrolling
        box so it stays put while pages scroll.
      */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          minHeight: 0,
          position: "relative",
        }}
      >
        <Box
          ref={attachViewport}
          data-testid="pdf-preview-viewport"
          onScroll={onScroll}
          sx={{
            position: "relative",
            flex: "1 1 auto",
            minHeight: pageSizes == null && failure == null ? LOADING_MIN_HEIGHT : 0,
            overflowX: "auto",
            overflowY: "auto",
            bgcolor: theme.palette.grey50.main,
            "& mark.pdf-search-match": {
              backgroundColor: alpha(theme.palette.primary.main, 0.25),
              color: "inherit",
              padding: 0,
            },
            "& mark.pdf-search-match[data-active='true']": {
              backgroundColor: alpha(theme.palette.primary.main, 0.6),
            },
          }}
        >
          {failure != null && <PreviewError message={FAILURE_MESSAGES[failure]} />}
          {/* Unmounted on failure, which also ends a load left waiting on a password. */}
          {failure == null && (
            <Document
              file={fileUrl}
              options={PDF_DOCUMENT_OPTIONS}
              onLoadSuccess={onLoadSuccess}
              onLoadError={onLoadError}
              onPassword={onPassword}
              // Internal links jump within the preview; external ones open away from Oak.
              onItemClick={({ pageIndex }) => goToPage(pageIndex)}
              externalLinkTarget="_blank"
              loading={
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  // Pinned to the viewport: react-pdf's wrapper has no height to fill.
                  sx={{ position: "absolute", inset: 0, height: "auto", borderRadius: 0.5 }}
                />
              }
              error={null}
              // Without this react-pdf suspends to, and throws its errors at, whatever
              // boundary sits above the dialog; pages inherit it through context.
              suspense={false}
            >
              {pageSizes?.map((size, pageIndex) => (
                <PdfPage
                  key={pageIndex}
                  pageIndex={pageIndex}
                  size={size}
                  zoom={zoom}
                  scrollRoot={pageClip}
                  highlights={highlights}
                  frameKey={frameKey}
                  attachFramedOutline={attachFramedOutline}
                  customTextRenderer={customTextRenderer}
                />
              ))}
            </Document>
          )}
        </Box>
        {bottomLeftButtonProps && (
          <Button
            variant="contained"
            {...bottomLeftButtonProps}
            sx={{
              position: "absolute",
              bottom: 16,
              left: 16,
              zIndex: 1,
              ...bottomLeftButtonProps.sx,
            }}
          >
            {bottomLeftButtonProps.text}
          </Button>
        )}
      </Box>
    </Box>
  );
});
